import { useEffect, useState, useCallback, useRef } from "react";
import { api, API } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  LogOut, Plus, Upload, Trash2, Images, FolderPlus, ShieldAlert, Loader2, ArrowLeft,
  UserPlus, ShieldCheck, Users, Lock, Shirt, ShoppingBag, FileText, Handshake, Newspaper, CreditCard, MessageCircle,
} from "lucide-react";
import logo from "../assets/logo.png";
import AdminPlantelTab from "./AdminPlantelTab";
import AdminTiendaTab from "./AdminTiendaTab";
import AdminContentTab from "./AdminContentTab";
import AdminSponsorsTab from "./AdminSponsorsTab";
import AdminNoticiasTab from "./AdminNoticiasTab";
import AdminMembresiasTab from "./AdminMembresiasTab";
import AdminContactoTab from "./AdminContactoTab";

const CATEGORIES = ["Partidos", "Festejos", "Inferiores", "Social / Quincho", "Histórico", "General"];

function LoginScreen() {
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `${API}/auth/google/login`;
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-navy px-4">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 sm:p-10 text-center shadow-2xl">
        <img src={logo} alt="Club" className="h-20 mx-auto" />
        <h1 className="mt-5 text-2xl font-bold text-navy">Panel de administración</h1>
        <p className="mt-2 text-gray-500">Ingresá con tu cuenta de Google autorizada del club.</p>
        <button
          data-testid="admin-google-login"
          onClick={login}
          className="mt-7 w-full inline-flex items-center justify-center gap-3 bg-navy text-white font-semibold py-3.5 rounded-xl hover:bg-[#16235a] transition-colors"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#fff" d="M12 11v2.8h4c-.2 1-1.3 2.9-4 2.9-2.4 0-4.4-2-4.4-4.5S9.6 7.7 12 7.7c1.4 0 2.3.6 2.8 1.1l1.9-1.8C15.5 5.8 13.9 5 12 5 8.1 5 5 8.1 5 12s3.1 7 7 7c4 0 6.7-2.8 6.7-6.8 0-.5 0-.8-.1-1.2H12z"/></svg>
          Continuar con Google
        </button>
        <a href="/" className="mt-5 inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-navy">
          <ArrowLeft className="h-4 w-4" /> Volver al sitio
        </a>
      </div>
    </div>
  );
}

function NotAuthorized({ email, onLogout }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-navy px-4">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 text-center shadow-2xl">
        <ShieldAlert className="h-12 w-12 mx-auto text-[#c0392b]" />
        <h1 className="mt-4 text-2xl font-bold text-navy">Acceso no autorizado</h1>
        <p className="mt-2 text-gray-500">La cuenta <strong>{email}</strong> no está habilitada como administrador del club.</p>
        <button onClick={onLogout} data-testid="admin-logout-unauth" className="mt-6 inline-flex items-center gap-2 bg-navy text-white px-5 py-3 rounded-xl font-semibold">
          <LogOut className="h-4 w-4" /> Cerrar sesión
        </button>
      </div>
    </div>
  );
}

function Panel({ user, onLogout }) {
  const [tab, setTab] = useState("galeria");
  const [albums, setAlbums] = useState([]);
  const [limits, setLimits] = useState(null);
  const [selected, setSelected] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const fileRef = useRef(null);

  const loadAlbums = useCallback(async () => {
    const [{ data }, lim] = await Promise.all([
      api.get("/gallery/albums"),
      api.get("/gallery/limits").then((r) => r.data).catch(() => null),
    ]);
    setAlbums(data);
    if (lim) setLimits(lim);
  }, []);

  const loadPhotos = useCallback(async (albumId) => {
    const { data } = await api.get(`/gallery/albums/${albumId}/photos`);
    setPhotos(data);
  }, []);

  useEffect(() => { loadAlbums(); }, [loadAlbums]);
  useEffect(() => { if (selected) loadPhotos(selected.id); }, [selected, loadPhotos]);

  const atLimit = limits && limits.albums_used >= limits.albums_max;

  const createAlbum = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/gallery/albums", {
        name,
        category,
        description,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        event_date: eventDate || null,
      });
      setName(""); setDescription(""); setTags(""); setEventDate("");
      toast.success("Álbum creado");
      loadAlbums();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo crear el álbum"); }
  };

  const saveMeta = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSavingMeta(true);
    const fd = new FormData(e.target);
    try {
      const { data } = await api.patch(`/gallery/albums/${selected.id}`, {
        name: fd.get("name"),
        category: fd.get("category"),
        description: fd.get("description"),
        tags: String(fd.get("tags") || "").split(",").map((t) => t.trim()).filter(Boolean),
        event_date: fd.get("event_date") || null,
      });
      setSelected(data);
      toast.success("Datos del álbum guardados");
      loadAlbums();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSavingMeta(false); }
  };

  const savePhotoCaption = async (photo, alt) => {
    if ((photo.alt || "") === alt) return;
    try {
      await api.patch(`/gallery/photos/${photo.id}`, { alt });
      setPhotos((ps) => ps.map((x) => (x.id === photo.id ? { ...x, alt } : x)));
      toast.success("Título de foto guardado");
    } catch { toast.error("No se pudo guardar el título"); }
  };

  const removeAlbum = async (id) => {
    if (!window.confirm("¿Eliminar este álbum y sus fotos?")) return;
    await api.delete(`/gallery/albums/${id}`);
    if (selected?.id === id) { setSelected(null); setPhotos([]); }
    toast.success("Álbum eliminado");
    loadAlbums();
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selected) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("album_id", selected.id);
      fd.append("alt", selected.name);
      try { await api.post("/gallery/photos", fd); ok++; }
      catch { /* skip */ }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    toast.success(`${ok} foto(s) subida(s)`);
    loadPhotos(selected.id);
    loadAlbums();
  };

  const removePhoto = async (id) => {
    await api.delete(`/gallery/photos/${id}`);
    setPhotos((p) => p.filter((x) => x.id !== id));
    toast.success("Foto eliminada");
  };

  return (
    <div className="min-h-screen bg-[#eef1f7]">
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Club" className="h-10" />
            <div className="leading-tight">
              <div className="font-bold" style={{ fontFamily: "Outfit" }}>Panel del Club</div>
              <div className="text-xs text-gold">Galería · Administración</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-white/70">{user.email}</span>
            <a href="/" className="text-sm text-white/80 hover:text-white">Ver sitio</a>
            <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg text-sm font-semibold">
              <LogOut className="h-4 w-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1">
          <button
            data-testid="tab-galeria"
            onClick={() => setTab("galeria")}
            className={`inline-flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${tab === "galeria" ? "border-gold text-navy" : "border-transparent text-gray-500 hover:text-navy"}`}
          >
            <Images className="h-4 w-4" /> Galería
          </button>
          <button
            data-testid="tab-plantel"
            onClick={() => setTab("plantel")}
            className={`inline-flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${tab === "plantel" ? "border-gold text-navy" : "border-transparent text-gray-500 hover:text-navy"}`}
          >
            <Shirt className="h-4 w-4" /> Plantel
          </button>
          <button
            data-testid="tab-tienda"
            onClick={() => setTab("tienda")}
            className={`inline-flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${tab === "tienda" ? "border-gold text-navy" : "border-transparent text-gray-500 hover:text-navy"}`}
          >
            <ShoppingBag className="h-4 w-4" /> Tienda
          </button>
          <button
            data-testid="tab-noticias"
            onClick={() => setTab("noticias")}
            className={`inline-flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${tab === "noticias" ? "border-gold text-navy" : "border-transparent text-gray-500 hover:text-navy"}`}
          >
            <Newspaper className="h-4 w-4" /> Noticias
          </button>
          <button
            data-testid="tab-membresias"
            onClick={() => setTab("membresias")}
            className={`inline-flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${tab === "membresias" ? "border-gold text-navy" : "border-transparent text-gray-500 hover:text-navy"}`}
          >
            <CreditCard className="h-4 w-4" /> Membresías
          </button>
          <button
            data-testid="tab-contacto"
            onClick={() => setTab("contacto")}
            className={`inline-flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${tab === "contacto" ? "border-gold text-navy" : "border-transparent text-gray-500 hover:text-navy"}`}
          >
            <MessageCircle className="h-4 w-4" /> Contacto
          </button>
          <button
            data-testid="tab-contenido"
            onClick={() => setTab("contenido")}
            className={`inline-flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${tab === "contenido" ? "border-gold text-navy" : "border-transparent text-gray-500 hover:text-navy"}`}
          >
            <FileText className="h-4 w-4" /> Contenido
          </button>
          <button
            data-testid="tab-patrocinadores"
            onClick={() => setTab("patrocinadores")}
            className={`inline-flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${tab === "patrocinadores" ? "border-gold text-navy" : "border-transparent text-gray-500 hover:text-navy"}`}
          >
            <Handshake className="h-4 w-4" /> Patrocinadores
          </button>
          <button
            data-testid="tab-admins"
            onClick={() => setTab("admins")}
            className={`inline-flex items-center gap-2 px-4 py-3.5 text-sm font-semibold border-b-2 transition-colors ${tab === "admins" ? "border-gold text-navy" : "border-transparent text-gray-500 hover:text-navy"}`}
          >
            <Users className="h-4 w-4" /> Administradores
          </button>
        </div>
      </div>

      {tab === "admins" && <AdminsManager />}
      {tab === "plantel" && <AdminPlantelTab />}
      {tab === "tienda" && <AdminTiendaTab />}
      {tab === "noticias" && <AdminNoticiasTab />}
      {tab === "membresias" && <AdminMembresiasTab />}
      {tab === "contacto" && <AdminContactoTab />}
      {tab === "contenido" && <AdminContentTab />}
      {tab === "patrocinadores" && <AdminSponsorsTab />}
      {tab === "galeria" && (
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-6">
        {/* Albums column */}
        <section className="lg:col-span-1">
          <form onSubmit={createAlbum} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-2 text-navy font-bold"><FolderPlus className="h-5 w-5 text-gold" /> Nuevo álbum</div>
            {limits && (
              <div data-testid="albums-limit" className={`mt-3 text-xs font-semibold rounded-lg px-3 py-2 ${atLimit ? "bg-[#c0392b]/10 text-[#c0392b]" : "bg-[#eef1f7] text-navy"}`}>
                {limits.albums_used} / {limits.albums_max} álbumes · máx {limits.photos_max} fotos c/u
                {atLimit && " — límite alcanzado"}
              </div>
            )}
            <input
              data-testid="album-name-input"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Nombre del álbum" className="mt-4 w-full border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy/30"
            />
            <select
              data-testid="album-category-select"
              value={category} onChange={(e) => setCategory(e.target.value)}
              className="mt-3 w-full border border-gray-200 rounded-lg px-3 py-2.5 bg-white"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
              data-testid="album-date-input"
              type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)}
              className="mt-3 w-full border border-gray-200 rounded-lg px-3 py-2.5 bg-white text-gray-600"
            />
            <input
              data-testid="album-tags-input"
              value={tags} onChange={(e) => setTags(e.target.value)}
              placeholder="Etiquetas (separadas por coma)" className="mt-3 w-full border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy/30"
            />
            <textarea
              data-testid="album-description-input"
              value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} placeholder="Descripción breve (opcional)"
              className="mt-3 w-full border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy/30 resize-none"
            />
            <button data-testid="album-create-btn" disabled={atLimit} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold py-2.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50 disabled:cursor-not-allowed">
              <Plus className="h-4 w-4" /> Crear álbum
            </button>
          </form>

          <div className="mt-5 space-y-2">
            {albums.map((a) => (
              <button
                key={a.id}
                data-testid={`album-item-${a.slug}`}
                onClick={() => setSelected(a)}
                className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                  selected?.id === a.id ? "bg-navy text-white border-navy" : "bg-white border-gray-100 hover:border-navy/30"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{a.name}</span>
                  <span className={`text-xs ${selected?.id === a.id ? "text-gold" : "text-gray-400"}`}>{a.photo_count} fotos</span>
                </div>
                <div className={`text-xs ${selected?.id === a.id ? "text-white/60" : "text-gray-400"}`}>{a.category}</div>
              </button>
            ))}
            {albums.length === 0 && <p className="text-sm text-gray-400 px-1">Todavía no hay álbumes.</p>}
          </div>
        </section>

        {/* Photos column */}
        <section className="lg:col-span-2">
          {!selected ? (
            <div className="h-full min-h-[300px] bg-white rounded-2xl border border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400">
              <Images className="h-10 w-10" />
              <p className="mt-3">Seleccioná un álbum para gestionar sus fotos</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-xl font-bold text-navy">{selected.name}</h2>
                  <p className="text-sm text-gray-400">{selected.category}</p>
                </div>
                <div className="flex items-center gap-2">
                  <label data-testid="upload-photos-btn" className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-4 py-2.5 rounded-lg cursor-pointer hover:opacity-90">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading ? "Subiendo…" : "Subir fotos"}
                    <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={uploading} />
                  </label>
                  <button onClick={() => removeAlbum(selected.id)} data-testid="delete-album-btn" className="inline-flex items-center gap-2 text-[#c0392b] border border-[#c0392b]/30 px-3 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#c0392b]/5">
                    <Trash2 className="h-4 w-4" /> Álbum
                  </button>
                </div>
              </div>

              <form key={selected.id} onSubmit={saveMeta} className="mt-4 border-t border-gray-100 pt-4 grid sm:grid-cols-2 gap-3">
                <input
                  data-testid="edit-album-name"
                  name="name" defaultValue={selected.name}
                  placeholder="Nombre del álbum" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
                <select
                  data-testid="edit-album-category"
                  name="category" defaultValue={selected.category}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input
                  data-testid="edit-album-date"
                  type="date" name="event_date" defaultValue={selected.event_date || ""}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 bg-white"
                />
                <input
                  data-testid="edit-album-tags"
                  name="tags" defaultValue={(selected.tags || []).join(", ")}
                  placeholder="Etiquetas (coma)" className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
                <textarea
                  data-testid="edit-album-description"
                  name="description" defaultValue={selected.description || ""}
                  rows={2} placeholder="Descripción del álbum"
                  className="sm:col-span-2 border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
                />
                <button data-testid="save-album-meta" disabled={savingMeta} className="sm:col-span-2 justify-self-start inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#16235a] disabled:opacity-50">
                  {savingMeta ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Guardar datos
                </button>
              </form>

              <div className="mt-5 flex items-center justify-between">
                <p className="text-sm font-semibold text-navy">Fotos</p>
                {limits && (
                  <span data-testid="photos-limit" className={`text-xs font-semibold ${photos.length >= limits.photos_max ? "text-[#c0392b]" : "text-gray-400"}`}>
                    {photos.length} / {limits.photos_max}
                  </span>
                )}
              </div>

              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {photos.map((p) => (
                  <div key={p.id} data-testid={`photo-${p.id}`} className="group">
                    <div className="relative aspect-square rounded-xl overflow-hidden border border-gray-100">
                      <img src={p.url} alt={p.alt} loading="lazy" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removePhoto(p.id)}
                        aria-label="Eliminar foto"
                        className="absolute top-2 right-2 h-8 w-8 rounded-lg bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#c0392b]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <input
                      data-testid={`photo-caption-${p.id}`}
                      defaultValue={p.alt || ""}
                      placeholder="Título / descripción"
                      onBlur={(e) => savePhotoCaption(p, e.target.value.trim())}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      className="mt-1.5 w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-navy/30"
                    />
                  </div>
                ))}
              </div>
              {photos.length === 0 && <p className="mt-6 text-sm text-gray-400">Sin fotos todavía. Subí las primeras con el botón de arriba.</p>}
            </div>
          )}
        </section>
      </main>
      )}
    </div>
  );
}

function AdminsManager() {
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admins"); setAdmins(data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await api.post("/admins", { email });
      setEmail("");
      toast.success("Administrador agregado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo agregar"); }
  };

  const remove = async (em) => {
    if (!window.confirm(`¿Quitar acceso de administrador a ${em}?`)) return;
    try { await api.delete(`/admins/${encodeURIComponent(em)}`); toast.success("Administrador quitado"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "No se pudo quitar"); }
  };

  return (
    <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <form onSubmit={add} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
        <div className="flex items-center gap-2 text-navy font-bold"><UserPlus className="h-5 w-5 text-gold" /> Agregar administrador</div>
        <p className="mt-1 text-sm text-gray-400">Ingresá el email de Google de la persona. Podrá entrar al panel con "Continuar con Google".</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <input
            data-testid="admin-email-input"
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="persona@gmail.com"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy/30"
          />
          <button data-testid="admin-add-btn" className="inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#16235a]">
            <Plus className="h-4 w-4" /> Agregar
          </button>
        </div>
      </form>

      <div className="mt-6 bg-white rounded-2xl border border-gray-100 p-2 shadow-sm">
        {admins.map((a) => (
          <div key={a.email} data-testid={`admin-row-${a.email}`} className="flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${a.type === "principal" ? "bg-navy text-gold" : "bg-[#eef1f7] text-navy"}`}>
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-navy truncate">{a.email}</div>
                <div className="text-xs text-gray-400">{a.type === "principal" ? "Administrador principal" : "Agregado"}</div>
              </div>
            </div>
            {a.type === "principal" ? (
              <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Lock className="h-3.5 w-3.5" /> Fijo</span>
            ) : (
              <button onClick={() => remove(a.email)} data-testid={`admin-remove-${a.email}`} aria-label="Quitar" className="h-9 w-9 rounded-lg text-[#c0392b] hover:bg-[#c0392b]/10 flex items-center justify-center">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </main>
  );
}

export default function AdminPage() {
  const [auth, setAuth] = useState(null); // null=loading, false=not authed, object=user

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setAuth(data);
    } catch {
      setAuth(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAuth(false);
    window.location.href = "/admin";
  };

  if (auth === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy text-white">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }
  if (auth === false) return <><Toaster position="top-center" /><LoginScreen /></>;
  if (!auth.is_admin) return <NotAuthorized email={auth.email} onLogout={logout} />;
  return <><Toaster position="top-center" richColors /><Panel user={auth} onLogout={logout} /></>;
}
